type Entries<T extends Record<string, any>> = {
  [K in keyof T]: [K, T[K]];
}[keyof T];

export type Instance =
  | 'automations_overview'
  | 'automations_condition_step'
  | 'broadcasts_schedule'
  | 'dashlets_dashboards'
  | 'dashlets_homepages'
  | 'page_builder_dynamic_images'
  | 'page_builder_dynamic_text'
  | 'record_details_related_object_block'
  | 'record_overview_contacts'
  | 'record_overview_custom_objects'
  | 'record_overview_team_member';

export type ObjectType =
  | 'client_client'
  | 'business_employee'
  | 'standard'
  | 'pipeline'
  | 'automation';

export type FilterVariables = {
  access: any;
  client_tag_field_id: string;
  custom_object_id: string;
  object_type: ObjectType;
  variable_values: FieldVariable[];
  fields_settings_search: boolean;
  variable_sources: FieldVariable[];
  instance: Instance;
};

export type FilterVariableEntry = Entries<FilterVariables>;

type ClientModelLike = {
  id: string;
  name: 'client_client';
  objectType: 'standard';
  undeletableFields: {
    tags: { id: string };
  };
};

type ObjectModelLike = {
  id: string;
  name: string;
  objectType: 'standard' | 'pipeline';
};

export type FieldVariable = {
  data_subtype?: {
    deleted: boolean;
    description: string;
    entity_name: string;
    id: string;
    name: string;
    object_name: string;
    object_type: 'client' | 'standard' | 'pipeline';
  };
  data_type:
    | 'boolean'
    | 'date'
    | 'datetime'
    | 'employee'
    | 'entity'
    | 'phone_number'
    | 'number'
    | 'string'
    | 'uuid';
  name: string;
};

export type GetFilterVariableOpts = {
  access?: any;
  clientTagId?: string;
  fieldSettingsSearch?: boolean;
  variableValues?: FieldVariable[];
  variableSources?: FieldVariable[];
  instance?: Instance;
};

const isClientModel = (obj: any): obj is ClientModelLike => {
  return obj && typeof obj === 'object' && obj.name === 'client_client';
};

const getObjectType = (
  model: ObjectModelLike | ClientModelLike
): ObjectType => {
  if (model.name === 'client_client' || model.name === 'business_employee') {
    return model.name;
  }

  return model.objectType;
};

/**
 * @remarks opts.access should usually be treated as required. You may choose to omit it if you only
 * care to resolve description values or something you know is not dependent on permissions. If the
 * model is _not_ the contact model, `opts.clientTagId` should be provided.
 */
export function getFilterVariables(
  model?: ObjectModelLike | ClientModelLike,
  opts?: GetFilterVariableOpts
): FilterVariableEntry[] {
  const result: FilterVariableEntry[] = [
    ['fields_settings_search', opts?.fieldSettingsSearch ?? false],
  ];

  if (model) {
    result.push(
      ['custom_object_id', model.id],
      ['object_type', getObjectType(model)]
    );
  }

  if (opts?.access) {
    result.push(['access', opts.access]);
  }

  if (opts?.variableValues) {
    result.push(['variable_values', opts.variableValues]);
  }

  if (opts?.variableSources) {
    result.push(['variable_sources', opts.variableSources]);
  }

  if (opts?.instance) {
    result.push(['instance', opts.instance]);
  }

  if (isClientModel(model)) {
    result.push(['client_tag_field_id', model.undeletableFields.tags.id]);
  } else if (opts?.clientTagId) {
    result.push(['client_tag_field_id', opts.clientTagId]);
  }

  return result;
}
